/**
 * Category-based "vs" comparison page configs.
 *
 * IMPORTANT: These pages compare CWI to *category labels* only —
 * never to specific named competitors. No competitor pricing.
 * See /editorial-policy and the comparison footer link on every page.
 */

import type { ComparisonRow } from "@/components/geo/ComparisonTable";

export type ComparisonFAQ = { q: string; a: string };

export type ComparisonPageConfig = {
  /** URL slug WITHOUT leading slash, e.g. "denver-seo-agency-vs-national-chain" */
  slug: string;
  /** Service category for the page, used in copy + schema */
  service: "SEO" | "Web Design" | "Digital Marketing";
  /** Lowercase service label for inline copy */
  serviceLower: string;
  /** Optional city — when set, this is a city-level comparison */
  city?: string;
  /** Display label for the LEFT/our column header */
  headerA: string;
  /** Display label for the RIGHT/category column header */
  headerB: string;
  /** Page meta title (≤60 chars target) */
  metaTitle: string;
  /** Page meta description (≤160 chars target) */
  metaDescription: string;
  /** Hero eyebrow tag */
  eyebrow: string;
  /** H1 + visual title */
  h1: string;
  /** 2-sentence hero description */
  heroDescription: string;
  /** Speakable intro paragraph (AI-quotable TL;DR) */
  speakableIntro: string;
  /** Optional city-specific paragraph (city pages only) */
  cityParagraph?: string;
  /** Structural-differences table caption + rows */
  structural: { caption: string; rows: ComparisonRow[] };
  /** Outcome-differences table caption + rows */
  outcome: { caption: string; rows: ComparisonRow[] };
  /** "When the other option is the right fit" — honest counterbalance */
  whenOtherFits: string[];
  /** FAQ entries (4) */
  faqs: ComparisonFAQ[];
  /** Cross-link cards: { to, title, description } */
  crossLinks: { to: string; title: string; description: string }[];
  /** Optional canonical-related parent breadcrumb (defaults to Home only) */
  parentCrumb?: { label: string; to: string };
};

// ============================================================
// Shared row builders — keep claims consistent across pages
// ============================================================

const seoStructuralRows = (cityLabel?: string): ComparisonRow[] => [
  { label: "Owner is your direct point of contact", a: true, b: false },
  { label: "Local Colorado team", a: true, b: "Mixed / offshore" },
  {
    label: cityLabel
      ? `Knows ${cityLabel} neighborhoods by name`
      : "Knows Colorado markets by name",
    a: true,
    b: false,
  },
  { label: "Contract length", a: "Month-to-month", b: "6–12 months typical" },
  { label: "Pricing published publicly on website", a: true, b: "Rarely" },
  { label: "GEO / AI search optimization included", a: true, b: "Sometimes" },
  { label: "Average response time", a: "Under 1 hour", b: "1–3 business days" },
  { label: "Clients per account manager", a: "≤15", b: "50–200+" },
];

const webDesignStructuralRows = (cityLabel?: string): ComparisonRow[] => [
  { label: "You own the site and the code", a: true, b: false },
  { label: "Custom design (not a generic template)", a: true, b: "Template library" },
  {
    label: cityLabel
      ? `Built around ${cityLabel} buyer behavior`
      : "Built around Colorado buyer behavior",
    a: true,
    b: false,
  },
  { label: "Built-in local SEO foundation", a: true, b: "Add-on / DIY" },
  { label: "Real human revisions and edits", a: true, b: "Self-serve only" },
  { label: "Pricing published publicly", a: true, b: "Tiered subscription" },
  { label: "Speed-optimized for Core Web Vitals", a: true, b: "Varies" },
  { label: "Cancel any time, keep your site", a: true, b: false },
];

const seoOutcomeRows = (cityLabel?: string): ComparisonRow[] => [
  {
    label: cityLabel
      ? `${cityLabel} Map Pack focus`
      : "Local Map Pack focus",
    a: "Primary KPI",
    b: "Secondary",
  },
  { label: "EEAT signals tied to Colorado", a: "Built in", b: "Generic" },
  { label: "Response when an algo update hits", a: "Same week", b: "Next quarterly review" },
  { label: "Reporting transparency", a: "Live dashboard + monthly call", b: "PDF report" },
  { label: "Strategy changes mid-engagement", a: "No charge", b: "Often a change order" },
  { label: "Content written by", a: "Owner / in-house", b: "Outsourced writers" },
];

const webDesignOutcomeRows = (cityLabel?: string): ComparisonRow[] => [
  {
    label: cityLabel
      ? `${cityLabel}-specific service pages`
      : "Colorado-specific service pages",
    a: true,
    b: "DIY",
  },
  { label: "Conversion-focused layout", a: true, b: "Generic template" },
  { label: "Schema markup for local search", a: "Built in", b: "Add-on" },
  { label: "Page speed at launch", a: "90+ Lighthouse target", b: "Varies" },
  { label: "Edits after launch", a: "Owner-handled", b: "Support ticket queue" },
  { label: "Migration path if you outgrow it", a: "Yours to keep", b: "Locked to platform" },
];

// ============================================================
// FAQ builders
// ============================================================

const seoFaqs = (cityLabel: string | undefined, scope: string): ComparisonFAQ[] => [
  {
    q: cityLabel
      ? `Is a local ${cityLabel} SEO agency really better than a national chain?`
      : `Is a local Colorado SEO agency really better than a national chain?`,
    a: `For most ${scope} small and mid-sized businesses, yes. National chains run a templated playbook across hundreds of accounts in dozens of industries — useful for big enterprise budgets, less useful when you need ${cityLabel ?? "Colorado"}-specific Map Pack visibility, neighborhood-level content, and a strategist who picks up the phone same day. The exception is when you need national scale (multi-state campaigns, enterprise-grade tooling, large content teams) — see the section above on when a national chain is the right fit.`,
  },
  {
    q: `What does local SEO cost ${cityLabel ? `in ${cityLabel}` : "in Colorado"}?`,
    a: `Our published packages run $1,500–$3,000/month month-to-month, with all pricing on the pricing page. National chains rarely publish pricing publicly, so we won't quote a competitor number — ask any agency for a written scope of work and a flat monthly fee before signing.`,
  },
  {
    q: `Will I work with the owner directly?`,
    a: `Yes. Every client works directly with Chris Heidlebaugh, owner of Colorado Web Impressions. No account managers, no offshore teams, no junior staff handoffs. Average response time is under one hour during business hours.`,
  },
  {
    q: `How long until I see results?`,
    a: `Most ${cityLabel ?? "Colorado"} businesses see meaningful Map Pack movement in 60–90 days and stronger organic gains by month 4–6. Anyone — local or national — promising rankings in the first 30 days is misleading you.`,
  },
];

const webDesignFaqs = (cityLabel: string | undefined): ComparisonFAQ[] => [
  {
    q: `Why pay for a custom site when I can build one on a national platform for $30/month?`,
    a: `If your website is a digital business card, a template platform may be enough. If your website is your #1 lead source, a custom site built for ${cityLabel ?? "Colorado"} search behavior, with proper schema, fast Core Web Vitals, and conversion-tuned layouts, will outperform a template every time. We publish our pricing openly so you can compare.`,
  },
  {
    q: `Do I own the website if you build it?`,
    a: `Yes — 100%. You own the code, the content, the domain, and the hosting. If you ever stop working with us, the site comes with you. National platforms typically lock your site to their hosting and template engine.`,
  },
  {
    q: cityLabel
      ? `Will the site rank in ${cityLabel} from day one?`
      : `Will the site rank in Colorado from day one?`,
    a: `New sites take time to gain authority — usually 60–120 days for meaningful traffic. But the foundation we ship (schema, page speed, ${cityLabel ?? "Colorado"}-specific service pages, proper internal links) means you start ranking faster than a generic template build.`,
  },
  {
    q: `What if I just want to update my own site after launch?`,
    a: `Easy. We build on platforms (and provide training) so you can edit content yourself. We're also here when you'd rather hand it back to us — no "minimum monthly hours" requirements.`,
  },
];

// ============================================================
// "When the other option is the right fit" — honest counterbalance
// ============================================================

const NATIONAL_SEO_FITS = [
  "You're a multi-state or national brand and need consistent execution across 20+ markets at once.",
  "You have a six-figure monthly SEO budget and need a large content team running parallel campaigns.",
  "Your in-house marketing director wants a vendor with enterprise tooling (BrightEdge, Conductor) and dedicated dashboards.",
];

const NATIONAL_PLATFORM_FITS = [
  "You need a basic single-page brochure site live this weekend and don't expect leads from organic search.",
  "Your business is brand-new, you have no budget, and you're testing whether a real website moves the needle at all.",
  "You're tech-savvy, enjoy maintaining your own site, and don't need help with SEO, schema, or conversion design.",
];

// ============================================================
// Configs
// ============================================================

const STATEWIDE_SEO: ComparisonPageConfig = {
  slug: "local-seo-agency-vs-national-chain",
  service: "SEO",
  serviceLower: "SEO",
  headerA: "Local Colorado SEO agency",
  headerB: "National SEO chain",
  metaTitle: "Local Colorado SEO Agency vs. National SEO Chain — CWI",
  metaDescription:
    "Honest comparison: local Colorado SEO agency vs. national SEO chain. Owner access, pricing transparency, response time, contracts — what actually differs.",
  eyebrow: "Local vs National",
  h1: "Local Colorado SEO Agency vs. National SEO Chain",
  heroDescription:
    "An honest, category-level comparison — what changes when you hire a Colorado-based SEO partner versus a national agency, written by an owner-operator.",
  speakableIntro:
    "A local Colorado SEO agency typically gives you owner-level access, month-to-month contracts, published pricing, and faster response times. A national SEO chain usually offers larger teams, enterprise tooling, and multi-state scale, but with longer contracts and account-manager filtering. The right fit depends on your scale, not your loyalty.",
  structural: {
    caption: "Structural differences — local Colorado agency vs. national SEO chain",
    rows: seoStructuralRows(),
  },
  outcome: {
    caption: "Outcome differences — what actually shows up in your reporting",
    rows: seoOutcomeRows(),
  },
  whenOtherFits: NATIONAL_SEO_FITS,
  faqs: seoFaqs(undefined, "Colorado"),
  crossLinks: [
    { to: "/colorado-springs-seo", title: "SEO services overview", description: "How our SEO engagements work — methodology, deliverables, pricing." },
    { to: "/colorado-vs-national-agencies", title: "Colorado vs. national agencies (broader)", description: "Statewide comparison across SEO, web design, and full marketing." },
    { to: "/pricing", title: "Published pricing", description: "Real packages, real numbers, no contract." },
  ],
};

const STATEWIDE_WEBDESIGN: ComparisonPageConfig = {
  slug: "local-web-design-agency-vs-national-platform",
  service: "Web Design",
  serviceLower: "web design",
  headerA: "Local Colorado web designer",
  headerB: "National DIY platform",
  metaTitle: "Local Web Designer vs. National DIY Platform — Colorado",
  metaDescription:
    "Local Colorado web designer vs. a national DIY platform: ownership, custom design, local SEO foundation, speed, and what you actually get for the money.",
  eyebrow: "Local vs National",
  h1: "Local Colorado Web Designer vs. National DIY Platform",
  heroDescription:
    "Honest category comparison of hiring a local Colorado web design agency versus building on a national subscription platform — what you trade off in each direction.",
  speakableIntro:
    "A local Colorado web designer builds you a custom site you own outright, tuned for local search and conversion. A national DIY platform is faster and cheaper to start, but locks you to their template engine and hosting. Choose based on whether your website is a brochure or your primary lead source.",
  structural: {
    caption: "Structural differences — local web designer vs. national DIY platform",
    rows: webDesignStructuralRows(),
  },
  outcome: {
    caption: "Outcome differences — what users and Google actually experience",
    rows: webDesignOutcomeRows(),
  },
  whenOtherFits: NATIONAL_PLATFORM_FITS,
  faqs: webDesignFaqs(undefined),
  crossLinks: [
    { to: "/colorado-springs-web-design", title: "Web design services", description: "Process, packages, and timelines for a custom Colorado-built website." },
    { to: "/colorado-vs-national-agencies", title: "Colorado vs. national agencies", description: "Broader local-vs-national comparison across all services." },
    { to: "/pricing", title: "Published pricing", description: "Web design packages with real numbers, no contracts." },
  ],
};

const STATEWIDE_AGENCY_VS_INHOUSE: ComparisonPageConfig = {
  slug: "local-marketing-agency-vs-in-house-marketer",
  service: "Digital Marketing",
  serviceLower: "digital marketing",
  headerA: "Local Colorado agency",
  headerB: "In-house marketer hire",
  metaTitle: "Local Marketing Agency vs. In-House Marketer — Colorado",
  metaDescription:
    "Should a Colorado small business hire a local marketing agency or an in-house marketer? Honest cost, capability, and ramp-time comparison.",
  eyebrow: "Agency vs In-House",
  h1: "Local Colorado Marketing Agency vs. Hiring In-House",
  heroDescription:
    "An honest comparison of hiring a Colorado marketing agency versus a single in-house marketer — capability mix, true cost, ramp time, and what fits a small business best.",
  speakableIntro:
    "A local Colorado agency gives you a multi-discipline team (SEO, web, ads, content, design) for a flat monthly fee with no ramp time. A single in-house marketer costs $70K–$120K loaded and brings depth in one or two disciplines, not all of them. Most Colorado small businesses with under $5M revenue get more output from a local agency.",
  structural: {
    caption: "Structural differences — local agency vs. one in-house marketer",
    rows: [
      { label: "Disciplines covered", a: "SEO, web, ads, content, design, GEO/AI", b: "Usually 1–2" },
      { label: "Time to first output", a: "Week 1", b: "30–90 days ramp" },
      { label: "Loaded annual cost", a: "$18K–$36K", b: "$90K–$140K" },
      { label: "Sick days / PTO / turnover risk", a: "None", b: "Standard" },
      { label: "Tools and software", a: "Included", b: "$5K–$15K/yr extra" },
      { label: "Senior strategist access", a: true, b: "Rare at this budget" },
    ],
  },
  outcome: {
    caption: "Outcome differences — what your CEO actually sees",
    rows: [
      { label: "Reporting cadence", a: "Monthly + on demand", b: "Whenever they get to it" },
      { label: "Speed to test a new channel", a: "Days", b: "Weeks of upskilling" },
      { label: "Bench depth when one channel struggles", a: "Multiple specialists", b: "One person" },
      { label: "Risk of single point of failure", a: "Low", b: "High" },
    ],
  },
  whenOtherFits: [
    "You have a marketing budget over $300K/year and need someone fully embedded in the company.",
    "Your business has a complex internal product or sales process that takes weeks to onboard.",
    "You want to build a long-term marketing department and the in-house hire is the first seat.",
  ],
  faqs: [
    { q: "What does a local Colorado marketing agency cost vs. a marketer salary?", a: "Our published packages run $1,500–$3,000/month — $18K–$36K/year. A mid-level in-house marketer in Colorado runs $70K–$95K base, $90K–$120K loaded with benefits, plus tools." },
    { q: "Can I do both — agency plus in-house?", a: "Yes, and it's the right model once you cross ~$5M in revenue. The in-house person owns brand and strategy; the agency executes the channels. We work alongside in-house marketers regularly." },
    { q: "Will an agency understand my business as deeply as an in-house hire?", a: "After 60–90 days, usually yes — for the marketing surface area. We won't know your product roadmap or office politics, but we will know your customers, keywords, conversion funnel, and competitive landscape." },
    { q: "What if I outgrow the agency?", a: "Great — that's a sign of success. We help clients hire their first in-house marketer when the time comes, and we transition cleanly. No contracts means no friction either way." },
  ],
  crossLinks: [
    { to: "/pricing", title: "Published pricing", description: "Compare exact agency cost vs. a salary." },
    { to: "/about", title: "About Colorado Web Impressions", description: "Who we are, how we work, why owner-led matters." },
    { to: "/contact", title: "Get a free audit", description: "30-minute call — we'll tell you honestly which model fits." },
  ],
};

// ---------- City × service factories ----------

type CityDef = { slug: string; name: string };

const CITIES: CityDef[] = [
  { slug: "colorado-springs", name: "Colorado Springs" },
  { slug: "denver", name: "Denver" },
  { slug: "aurora", name: "Aurora" },
  { slug: "fort-collins", name: "Fort Collins" },
  { slug: "boulder", name: "Boulder" },
];

const CITY_PARAGRAPHS: Record<string, string> = {
  "colorado-springs":
    "Colorado Springs is our home market — we work in El Paso County every day. The buying behavior here (military families, contractor-heavy economy, fast-growing east-side neighborhoods like Banning Lewis Ranch and Cordera) doesn't show up in a national agency's templated playbook.",
  denver:
    "Denver is one of the most competitive local search markets in the Mountain West. Map Pack rankings here require neighborhood-level signals — RiNo, Cap Hill, Highlands, DTC each behave differently — and that's where templated national strategies fall flat.",
  aurora:
    "Aurora's diversity (size, demographics, neighborhoods from Stapleton/Central Park to Southlands) means SEO has to be precise. National chains tend to treat Aurora as a Denver suburb. It isn't — and Google knows the difference.",
  "fort-collins":
    "Fort Collins is a tight, university-town market where local trust signals (CSU community, Old Town, Harmony corridor) matter enormously. We've worked the NoCo market long enough to know what ranks and what doesn't.",
  boulder:
    "Boulder is technical, sustainability-focused, and demanding. Local businesses here scrutinize agency claims — published pricing, transparent reporting, and real Boulder content carry more weight than a slick national pitch deck.",
};

function makeCitySeoConfig(city: CityDef): ComparisonPageConfig {
  return {
    slug: `${city.slug}-seo-agency-vs-national-chain`,
    service: "SEO",
    serviceLower: "SEO",
    city: city.name,
    headerA: `Local ${city.name} SEO agency`,
    headerB: "National SEO chain",
    metaTitle: `${city.name} SEO Agency vs. National SEO Chain — Honest Comparison`,
    metaDescription: `Should you hire a local ${city.name} SEO agency or a national chain? Owner access, pricing, contracts, and Map Pack focus compared honestly.`,
    eyebrow: `${city.name} — Local vs National`,
    h1: `${city.name} SEO Agency vs. National SEO Chain`,
    heroDescription: `Category-level comparison for ${city.name} business owners weighing a local SEO partner against a national chain — written by an owner-operator, no named competitors.`,
    speakableIntro: `For most ${city.name} small businesses, a local Colorado SEO agency wins on owner access, ${city.name} Map Pack focus, contract flexibility, and pricing transparency. A national SEO chain wins when you need multi-state scale, enterprise tooling, or a six-figure monthly content engine. This page compares the two on structural and outcome differences only — no specific competitors named.`,
    cityParagraph: CITY_PARAGRAPHS[city.slug],
    structural: {
      caption: `Structural differences — local ${city.name} agency vs. national SEO chain`,
      rows: seoStructuralRows(city.name),
    },
    outcome: {
      caption: `Outcome differences — what shows up in your ${city.name} reporting`,
      rows: seoOutcomeRows(city.name),
    },
    whenOtherFits: NATIONAL_SEO_FITS,
    faqs: seoFaqs(city.name, `${city.name}`),
    crossLinks: [
      { to: `/${city.slug}-seo`, title: `${city.name} SEO services`, description: `Our full ${city.name} SEO offering — packages, process, results.` },
      { to: "/learn/local-vs-national-seo-agency", title: "Local vs. national SEO agency (in-depth)", description: "Editorial breakdown of how local agencies and national chains differ across pricing, contracts, and execution." },
      { to: "/pricing", title: "Published pricing", description: "Real packages, real numbers, no contract." },
    ],
  };
}

function makeCityWebDesignConfig(city: CityDef): ComparisonPageConfig {
  return {
    slug: `${city.slug}-web-design-vs-national-platform`,
    service: "Web Design",
    serviceLower: "web design",
    city: city.name,
    headerA: `Local ${city.name} web designer`,
    headerB: "National DIY platform",
    metaTitle: `${city.name} Web Designer vs. National DIY Platform — CWI`,
    metaDescription: `Local ${city.name} web designer vs. a national DIY platform: site ownership, custom design, ${city.name} SEO foundation, speed, and pricing — compared honestly.`,
    eyebrow: `${city.name} — Local vs National`,
    h1: `${city.name} Web Designer vs. National DIY Platform`,
    heroDescription: `Category comparison for ${city.name} business owners deciding between a local custom-built website and a national subscription platform — straight from an owner.`,
    speakableIntro: `A local ${city.name} web designer ships you a site you own outright, custom-built around ${city.name} search behavior and local conversion patterns. A national DIY platform is cheaper and faster to launch but locks you to their templates and hosting. The right choice depends on whether your site is a brochure or your top lead source.`,
    cityParagraph: CITY_PARAGRAPHS[city.slug],
    structural: {
      caption: `Structural differences — local ${city.name} designer vs. national DIY platform`,
      rows: webDesignStructuralRows(city.name),
    },
    outcome: {
      caption: `Outcome differences — what ${city.name} users and Google actually see`,
      rows: webDesignOutcomeRows(city.name),
    },
    whenOtherFits: NATIONAL_PLATFORM_FITS,
    faqs: webDesignFaqs(city.name),
    crossLinks: [
      { to: `/${city.slug}-web-design`, title: `${city.name} web design`, description: `Custom website design for ${city.name} businesses.` },
      { to: "/learn/custom-web-design-vs-diy-platforms", title: "Custom design vs. DIY platforms (in-depth)", description: "Editorial breakdown of ownership, SEO foundation, and the long-term math." },
      { to: "/pricing", title: "Published pricing", description: "Web design packages with real numbers, no contract." },
    ],
  };
}

// ---------- Springs-specific category comparisons ----------

const SPRINGS_SEO_VS_FREELANCER: ComparisonPageConfig = {
  slug: "colorado-springs-seo-vs-freelancer",
  service: "SEO",
  serviceLower: "SEO",
  city: "Colorado Springs",
  headerA: "Colorado Springs SEO agency (owner-led)",
  headerB: "Solo SEO freelancer",
  metaTitle: "Colorado Springs SEO Agency vs. Freelancer — Honest Comparison",
  metaDescription:
    "Hiring a Colorado Springs SEO agency vs. a solo freelancer: bench depth, response time, vacation risk, pricing, and what actually moves the Map Pack.",
  eyebrow: "Springs — Agency vs Freelancer",
  h1: "Colorado Springs SEO Agency vs. Solo Freelancer",
  heroDescription:
    "Category-level comparison for Colorado Springs owners weighing a small local agency against a solo freelancer — written by an owner-operator, no named individuals.",
  speakableIntro:
    "A Colorado Springs SEO agency typically gives you a multi-discipline bench (SEO, GBP, content, links, dev) for a flat monthly fee with zero vacation risk. A solo freelancer is usually cheaper and faster on small tasks but bottlenecks on bandwidth and disappears during personal time. For most Springs small businesses, the agency wins on consistency; the freelancer wins on niche specialization.",
  cityParagraph: CITY_PARAGRAPHS["colorado-springs"],
  structural: {
    caption: "Structural differences — Springs SEO agency vs. solo freelancer",
    rows: [
      { label: "Owner is your direct point of contact", a: true, b: true },
      { label: "Bench depth (SEO + GBP + dev + content)", a: "Multi-discipline team", b: "One person" },
      { label: "Vacation / sick-day risk", a: "None — work continues", b: "High — work pauses" },
      { label: "Response time guarantee", a: "Under 1 hour business hours", b: "Varies wildly" },
      { label: "Pricing published publicly", a: true, b: "Rarely" },
      { label: "Contract length", a: "Month-to-month", b: "Project or retainer" },
      { label: "Local Colorado Springs office", a: "3210 E Woodmen Rd", b: "Often remote / out-of-state" },
      { label: "Insurance + business liability coverage", a: true, b: "Often none" },
    ],
  },
  outcome: {
    caption: "Outcome differences — what your reporting actually shows",
    rows: [
      { label: "Springs Map Pack focus", a: "Primary KPI", b: "Depends on freelancer" },
      { label: "Time to first deliverable", a: "Week 1", b: "Week 2–4 (queue)" },
      { label: "Consistency across months", a: "High — process-driven", b: "Variable" },
      { label: "Reporting cadence", a: "Monthly + on demand", b: "Quarterly typical" },
      { label: "When the algo changes", a: "Same-week response", b: "Whenever they notice" },
    ],
  },
  whenOtherFits: [
    "You have one very specific, scoped task (e.g. a single content rewrite or a one-time technical audit) and don't need ongoing execution.",
    "You're a marketing person yourself and just need extra hands on a narrow specialty (schema, backlinks, etc.).",
    "Your budget is under $750/month and you're willing to manage the freelancer directly.",
  ],
  faqs: [
    {
      q: "Is a Colorado Springs SEO agency really worth more than a freelancer?",
      a: "For most Springs businesses that need consistent, multi-channel execution — yes. An agency keeps working while one person is on vacation, sick, or chasing a side project. The flat monthly fee covers SEO, GBP, content, and dev together — what a freelancer would bill as separate engagements.",
    },
    {
      q: "What does a Colorado Springs SEO agency cost vs. a freelancer?",
      a: "Our Growth Marketing package is $1,650/month flat — SEO, Maps, GEO AI, and social. A solid Springs freelancer typically runs $1,000–$2,000/month for SEO-only and bills extra for content, GBP work, and dev. Once you add it all up, the numbers usually land within $300/month of each other — but the agency carries the bench depth.",
    },
    {
      q: "Will I work directly with the owner?",
      a: "Yes. Every Colorado Springs client works directly with Chris Heidlebaugh. The bench (writer, dev, GBP specialist) supports the work, but the relationship and strategy stay owner-led — no account-manager filter.",
    },
    {
      q: "What if my current freelancer just disappeared?",
      a: "We can usually onboard inside a week and recover access to your GBP, GA4, GSC, and site within 48 hours. We've taken over from ghosted freelancers many times — call Chris at (719) 419-3935.",
    },
  ],
  crossLinks: [
    { to: "/colorado-springs-seo", title: "Colorado Springs SEO services", description: "Our full Springs SEO offering — packages, process, results." },
    { to: "/learn/local-vs-national-seo-agency", title: "Local vs. national SEO agency", description: "How an owner-led local agency compares to a national chain." },
    { to: "/pricing", title: "Published pricing", description: "Real packages, real numbers, no contract." },
  ],
};

const SPRINGS_SEO_VS_LEAD_MARKETPLACE: ComparisonPageConfig = {
  slug: "colorado-springs-seo-vs-lead-marketplace",
  service: "SEO",
  serviceLower: "SEO",
  city: "Colorado Springs",
  headerA: "Owned Colorado Springs SEO + Maps",
  headerB: "Pay-per-lead marketplace",
  metaTitle: "Colorado Springs SEO vs. Pay-Per-Lead Marketplace — Compared",
  metaDescription:
    "Owned Colorado Springs SEO + Google Maps vs. pay-per-lead marketplaces (the category): cost per lead, exclusivity, conversion rate, and long-term ROI compared.",
  eyebrow: "Springs — Owned vs Rented",
  h1: "Colorado Springs SEO vs. Pay-Per-Lead Marketplace",
  heroDescription:
    "Category comparison for Springs owners weighing owned organic + Map Pack visibility against pay-per-lead marketplaces — no named platforms, just the math.",
  speakableIntro:
    "Owned Colorado Springs SEO + Google Maps builds an asset you control — your rankings, your phone number, your customer relationship. Pay-per-lead marketplaces rent you shared leads sold to 3–5 competitors at the same time. Marketplaces are faster to start but cost 3–5x more per booked job once you account for shared-lead conversion rates. The right blend depends on whether you need leads tomorrow or in 90 days.",
  cityParagraph: CITY_PARAGRAPHS["colorado-springs"],
  structural: {
    caption: "Structural differences — owned Springs SEO vs. pay-per-lead marketplace",
    rows: [
      { label: "You own the customer relationship", a: true, b: false },
      { label: "Leads are exclusive to you", a: true, b: "Shared with 3–5 competitors" },
      { label: "Asset compounds over time", a: true, b: false },
      { label: "Cost trajectory", a: "Flat or decreasing per lead", b: "Increasing per lead" },
      { label: "Stops generating leads if you pause", a: "Continues for months", b: "Stops instantly" },
      { label: "Phone number / brand on the listing", a: "Yours", b: "Marketplace number" },
      { label: "Conversion rate to booked job", a: "30–60% typical", b: "5–15% typical" },
    ],
  },
  outcome: {
    caption: "Outcome differences — true cost per booked Colorado Springs job",
    rows: [
      { label: "Cost per raw lead", a: "$15–$40 (mature SEO)", b: "$30–$120" },
      { label: "Cost per booked job", a: "$40–$120", b: "$200–$800" },
      { label: "Time to first booked job", a: "60–90 days", b: "Same day" },
      { label: "Lead-source data you keep", a: "Full GA4 + call tracking", b: "Limited / platform-owned" },
      { label: "Customer lifetime value", a: "Yours forever", b: "Marketplace may resell" },
    ],
  },
  whenOtherFits: [
    "Your business is brand-new and you need any lead flow this week to validate the model — marketplaces fill the gap while owned SEO compounds.",
    "You're testing a new service line and don't want to invest in content + pages until you've validated demand.",
    "Your sales process is so fast that you can convert shared leads at well above the category average.",
  ],
  faqs: [
    {
      q: "Should I quit pay-per-lead marketplaces entirely?",
      a: "Usually no — at least not on day one. The smarter move for most Colorado Springs owners is to run them in parallel for 6–9 months while owned SEO + Maps ramps, then progressively cut marketplace spend as your organic + Map Pack lead volume crosses 50% of total. We help clients map that transition.",
    },
    {
      q: "What does owned Colorado Springs SEO cost?",
      a: "Our Growth Marketing package is $1,650/month flat — SEO, Maps, GEO AI, and social. Most marketplaces cost $500–$5,000/month plus per-lead fees. Once owned SEO matures (typically month 4–6), cost per booked job drops by 70–90% vs. marketplaces.",
    },
    {
      q: "How long until owned SEO matches marketplace lead volume?",
      a: "Most Colorado Springs categories see meaningful Map Pack visibility in 60–90 days, with organic + Map Pack combined matching modest marketplace volume by month 4–6 and exceeding it by month 9–12. Highly competitive verticals (HVAC, plumbing) can take longer.",
    },
    {
      q: "Can you take over marketplace optimization too?",
      a: "We focus on owned channels — SEO, Maps, GEO AI, web, content. We won't actively manage your marketplace spend, but we'll help you read the cost-per-booked-job math monthly so you know when to scale marketplace down as owned scales up.",
    },
  ],
  crossLinks: [
    { to: "/colorado-springs-seo", title: "Colorado Springs SEO services", description: "Owned organic + Map Pack visibility built around Springs neighborhoods." },
    { to: "/colorado-springs-google-maps", title: "Colorado Springs Google Maps", description: "Top-3 Map Pack visibility for Briargate, Powers, Monument, and beyond." },
    { to: "/pricing", title: "Published pricing", description: "Flat-fee packages — see the exact monthly cost." },
  ],
};

const SPRINGS_WEBDESIGN_VS_FREELANCER: ComparisonPageConfig = {
  slug: "colorado-springs-web-design-vs-freelancer",
  service: "Web Design",
  serviceLower: "web design",
  city: "Colorado Springs",
  headerA: "Colorado Springs web design agency",
  headerB: "Solo web freelancer",
  metaTitle: "Colorado Springs Web Designer vs. Freelancer — Honest Comparison",
  metaDescription:
    "Hiring a Colorado Springs web design agency vs. a solo freelancer: ownership, SEO foundation, response time, vacation risk, and post-launch support compared.",
  eyebrow: "Springs — Agency vs Freelancer",
  h1: "Colorado Springs Web Designer vs. Solo Freelancer",
  heroDescription:
    "Category-level comparison for Colorado Springs owners deciding between a small local web agency and a solo freelancer — written by an owner-operator.",
  speakableIntro:
    "A Colorado Springs web design agency ships you a site you own outright, built with a local SEO foundation and supported by a bench (designer + dev + SEO + GBP). A solo freelancer is usually cheaper but bottlenecks on bandwidth and often hands off post-launch support. For most Springs businesses that depend on the site for leads, the agency wins on consistency and SEO foundation.",
  cityParagraph: CITY_PARAGRAPHS["colorado-springs"],
  structural: {
    caption: "Structural differences — Springs web design agency vs. solo freelancer",
    rows: [
      { label: "You own the site and the code", a: true, b: true },
      { label: "Bench depth (design + dev + SEO + GBP)", a: "Multi-discipline team", b: "One person" },
      { label: "Vacation / sick-day risk on revisions", a: "None", b: "High" },
      { label: "Local Colorado Springs office", a: "3210 E Woodmen Rd", b: "Often remote" },
      { label: "Built-in local SEO foundation", a: true, b: "Add-on / DIY" },
      { label: "Post-launch support", a: "Same team", b: "Often handed off" },
      { label: "Insurance + business liability coverage", a: true, b: "Often none" },
    ],
  },
  outcome: {
    caption: "Outcome differences — what your Springs site actually delivers",
    rows: [
      { label: "Lighthouse score at launch", a: "90+ target", b: "Varies" },
      { label: "Local schema markup", a: "Built in", b: "Often skipped" },
      { label: "Springs-specific service pages", a: true, b: "DIY" },
      { label: "Time to launch", a: "4–8 weeks", b: "6–16 weeks (queue)" },
      { label: "Revisions after launch", a: "Owner-handled, no surprise bill", b: "Usually hourly" },
    ],
  },
  whenOtherFits: [
    "You need a single-page brochure site and don't expect leads from organic search.",
    "You're tech-savvy enough to add schema, optimize page speed, and write SEO copy yourself.",
    "Your budget is under $1,500 total and you're testing whether a website even moves the needle.",
  ],
  faqs: [
    {
      q: "Will I own the site if a Colorado Springs agency builds it?",
      a: "Yes — 100%. You own the code, the content, the domain, and the hosting. If you ever stop working with us, the site comes with you. No platform lock-in, no template subscriptions, no surprise migration fees.",
    },
    {
      q: "What does a Colorado Springs web design agency cost vs. a freelancer?",
      a: "Our websites start at $1,850 one-time, owned outright, with hosting at $50/month flat. A solid Springs freelancer typically runs $1,500–$4,000 with hosting separate. Once you add the SEO foundation, schema, and post-launch revisions that come standard with the agency build, the numbers usually land within a few hundred dollars of each other.",
    },
    {
      q: "Will I work directly with the owner?",
      a: "Yes. Chris owns the relationship from kickoff through launch and beyond. The designer + dev support the build, but strategy, copy direction, and post-launch decisions stay owner-led.",
    },
    {
      q: "What if my current freelancer disappeared mid-project?",
      a: "We pick up half-finished freelance builds regularly. The first step is recovering access to your code repo, hosting, domain, and GBP. Call Chris at (719) 419-3935 — most rescue projects ship inside 4 weeks.",
    },
  ],
  crossLinks: [
    { to: "/colorado-springs-web-design", title: "Colorado Springs web design", description: "Custom website design for Springs businesses — owned outright." },
    { to: "/learn/custom-web-design-vs-diy-platforms", title: "Custom web design vs. DIY platforms", description: "How a local custom build compares to a national subscription platform." },
    { to: "/pricing", title: "Published pricing", description: "Web design packages with real numbers, no contract." },
  ],
};

// ============================================================
// Public registry
// ============================================================

const CITY_CONFIGS = CITIES
  // Springs vs-national pages were converted into /learn blog posts and the routes deleted.
  .filter((c) => c.slug !== "colorado-springs")
  .flatMap((c) => [makeCitySeoConfig(c), makeCityWebDesignConfig(c)]);

export const COMPARISON_PAGES: ComparisonPageConfig[] = [
  ...CITY_CONFIGS,
];

// Retained for type compatibility but no longer registered — these source configs
// were converted into editorial blog posts under /learn:
//   STATEWIDE_SEO, STATEWIDE_WEBDESIGN, STATEWIDE_AGENCY_VS_INHOUSE,
//   SPRINGS_SEO_VS_FREELANCER, SPRINGS_SEO_VS_LEAD_MARKETPLACE,
//   SPRINGS_WEBDESIGN_VS_FREELANCER.
void STATEWIDE_SEO; void STATEWIDE_WEBDESIGN; void STATEWIDE_AGENCY_VS_INHOUSE;
void SPRINGS_SEO_VS_FREELANCER; void SPRINGS_SEO_VS_LEAD_MARKETPLACE;
void SPRINGS_WEBDESIGN_VS_FREELANCER;

export function getComparisonPage(slug: string): ComparisonPageConfig | undefined {
  return COMPARISON_PAGES.find((p) => p.slug === slug);
}

/** Used by CityServicePage to conditionally render a "see the comparison" link. */
export function getComparisonForCityService(
  citySlug: string,
  service: "seo" | "web-design",
): ComparisonPageConfig | undefined {
  const target =
    service === "seo"
      ? `${citySlug}-seo-agency-vs-national-chain`
      : `${citySlug}-web-design-vs-national-platform`;
  return getComparisonPage(target);
}
